import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Backdrop, Grow, Modal } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import swal2 from 'sweetalert2';
import Config from '../../commons/config/Config.js';
import * as Utils from '../../commons/utils/Utils';

const EXTENSIONES = ['xlsx', 'csv'];

const escapeHtml = (texto) => String(texto)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const extensionDe = (archivo) => {
  const nombre = archivo && archivo.name ? archivo.name : '';
  return nombre.indexOf('.') === -1 ? '' : nombre.split('.').pop().toLowerCase();
};

// VEC-4382: importa por planilla (.xlsx / .csv) los Servicios o los Ítems por servicio del proveedor. Es una
// importación parcial: las filas válidas se aplican y las que fallan se listan con su motivo. Si la planilla deja
// algún servicio sin ningún ítem de un tipo, el backend no escribe nada y pide confirmar: ese tipo queda en
// "todos los ítems" (VEC-3947), y se reenvía la misma planilla confirmada.
// En el alta (sin `gerenciadorId`) el proveedor todavía no existe: la importación no guarda nada, parte de lo que tiene
// el formulario (`datosDelFormulario()` -> {servicios, serviciosItems}) y `onImportado(datos)` recibe lo que queda.
const GerenciadorPlanillaImportModal = ({ open, tipo, gerenciadorId, gerenciadorNombre, datosDelFormulario, onClose, onImportado }) => {
  const intl = useIntl();
  const inputArchivo = useRef(null);
  const [archivo, setArchivo] = useState(null);
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [errorGeneral, setErrorGeneral] = useState(null);

  const esItems = tipo === 'items';
  // En el alta todavía no hay razón social que mostrar.
  const nombreProveedor = gerenciadorNombre || intl.formatMessage({ id: 'gerenciadorPlanilla.proveedor_nuevo', defaultMessage: 'Proveedor nuevo' });

  const traducir = (clave, contexto) => Utils.translateBackendKey(clave, contexto || null, intl);

  const cerrar = () => {
    setArchivo(null);
    setResultado(null);
    setErrorGeneral(null);
    setImportando(false);
    onClose();
  };

  // Cerrar desde la UI (X, Cancelar, Escape, click afuera) no se permite mientras se importa: el backend ya está
  // aplicando (o aplicó) la importación, y un modal cerrado a mitad de camino dejaría al ABM con los servicios e
  // ítems viejos hasta que el usuario guarde otra cosa y la deshaga. El cierre automático al terminar usa cerrar().
  const cerrarPorUsuario = () => {
    if (importando) return;
    cerrar();
  };

  const handleArchivo = (event) => {
    const elegido = event.target.files && event.target.files.length > 0 ? event.target.files[0] : null;
    setArchivo(elegido);
    setResultado(null);
    setErrorGeneral(elegido && EXTENSIONES.indexOf(extensionDe(elegido)) === -1
      ? traducir('gerenciadorPlanilla.errors.formato_invalido')
      : null);
  };

  const tituloDeTipo = (clave) => (clave === 'Repuesto'
    ? intl.formatMessage({ id: 'gerenciadorPlanilla.tipo.repuestos', defaultMessage: 'Repuestos' })
    : intl.formatMessage({ id: 'gerenciadorPlanilla.tipo.mano_de_obra', defaultMessage: 'Mano de Obra' }));

  // Los tipos que la planilla deja sin ningún ítem: pasarían a "todos". Hay que decirlo antes de guardar.
  const confirmarSinItems = (grupos) => {
    const detalle = grupos.map((grupo) => intl.formatMessage(
      { id: 'gerenciadorPlanilla.confirmar.detalle', defaultMessage: 'Para {servicio} ({tipo}) la planilla no incluye ningún ítem.' },
      { servicio: '<b>' + escapeHtml(grupo.servicio) + '</b>', tipo: escapeHtml(tituloDeTipo(grupo.tipo)) }
    )).join('<br/>');
    const consecuencia = intl.formatMessage(
      {
        id: 'gerenciadorPlanilla.confirmar.consecuencia',
        defaultMessage: 'Si continúa, el proveedor quedará <b>sin selección</b> y pasará a brindar <b>todos</b> los ítems de ese tipo del servicio.',
      },
      { b: (chunks) => '<b>' + chunks.join('') + '</b>' }
    );

    return swal2.fire({
      title: intl.formatMessage({ id: 'gerenciadorPlanilla.confirmar.titulo', defaultMessage: '¿Confirma la importación?' }),
      html: detalle + '<br/>' + (Array.isArray(consecuencia) ? consecuencia.join('') : consecuencia),
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#0ABFBC',
      cancelButtonColor: '#d33',
      confirmButtonText: intl.formatMessage({ id: 'gerenciadorPlanilla.confirmar.si_importar', defaultMessage: 'Sí, importar' }),
      cancelButtonText: intl.formatMessage({ id: 'gerenciadorPlanilla.confirmar.cancelar', defaultMessage: 'Cancelar' }),
    }).then((respuesta) => !!respuesta.value);
  };

  const sincronizacionFallida = () => intl.formatMessage({
    id: 'gerenciadorPlanilla.error_sincronizar',
    defaultMessage: 'La planilla se importó, pero no se pudo actualizar la pantalla. Recargue la página antes de guardar, para no deshacer lo importado.',
  });

  const procesar = (datos) => {
    if (datos.requiereConfirmacion) {
      setImportando(false);
      return confirmarSinItems(datos.grupos || []).then((confirmado) => (confirmado ? enviar(true) : null));
    }

    // El ABM tiene que mostrar lo importado ANTES de informar el resultado: si el usuario guardara el formulario
    // con los servicios / ítems viejos, desharía la importación. Hasta entonces el modal sigue ocupado.
    const sincronizado = datos.importadas > 0 && onImportado
      ? Promise.resolve().then(() => onImportado(datos))
      : Promise.resolve();

    return sincronizado.then(
      () => {
        setImportando(false);
        if (!datos.errores || datos.errores.length === 0) {
          cerrar();
          return swal2.fire(intl.formatMessage({ id: 'gerenciadorPlanilla.exito', defaultMessage: 'Importación realizada con Éxito!' }), ' ', 'success');
        }
        setResultado(datos);
        return null;
      },
      () => {
        setImportando(false);
        if (!gerenciadorId) {
          // Alta: no se escribió nada y el formulario quedó como estaba; basta con volver a importar.
          setErrorGeneral(intl.formatMessage({
            id: 'gerenciadorPlanilla.error_sincronizar_alta',
            defaultMessage: 'No se pudieron cargar los ítems importados, por eso no se aplicó nada al formulario. Vuelva a intentarlo.',
          }));
          return null;
        }
        setResultado(datos);
        setErrorGeneral(sincronizacionFallida());
        return null;
      }
    );
  };

  const enviar = (confirmar) => {
    const formData = new FormData();
    formData.append('file', archivo);
    if (confirmar) formData.append('confirmar', '1');
    if (!gerenciadorId && datosDelFormulario) {
      const borrador = datosDelFormulario();
      formData.append('servicios', JSON.stringify(borrador.servicios || []));
      formData.append('serviciosItems', JSON.stringify(borrador.serviciosItems || {}));
    }

    setImportando(true);
    return axios({
      method: 'POST',
      url: Config.get('apiUrlBase') + '/gerenciadores/' + (gerenciadorId || 'nuevo') + '/' + tipo + '/importar',
      data: formData,
      // Sin Content-Type explícito: el navegador tiene que poner el boundary del multipart.
      headers: { 'Authorization-Token': localStorage.getItem('token') },
    })
      .then((res) => procesar(res.data))
      .catch((err) => {
        const datos = err && err.response ? err.response.data : null;
        setImportando(false);
        // Dos formas de 400: {mensaje, context} (errores del servicio, con clave i18n) y {codigo, mensaje, detalle}
        // (ValidateRequestMiddleware), cuyo `mensaje` es genérico y el `detalle` trae el motivo (claves i18n).
        const detalle = datos && Array.isArray(datos.detalle) && datos.detalle.length
          ? datos.detalle.map((motivo) => traducir(motivo)).join(' ')
          : null;
        setErrorGeneral(detalle || (datos && datos.mensaje
          ? traducir(datos.mensaje, datos.context)
          : intl.formatMessage({ id: 'gerenciadorPlanilla.error_desconocido', defaultMessage: 'No se pudo procesar el archivo.' })));
      });
  };

  const handleImportar = () => {
    if (!archivo || errorGeneral) return;
    setResultado(null);
    enviar(false);
  };

  const etiquetaCampo = (campo) => {
    switch (campo) {
      case 'tipo':
        return intl.formatMessage({ id: 'gerenciadorPlanilla.columna.tipo', defaultMessage: 'Tipo' });
      case 'item':
        return intl.formatMessage({ id: 'gerenciadorPlanilla.columna.item', defaultMessage: 'Ítem' });
      case 'provee':
        return intl.formatMessage({ id: 'gerenciadorPlanilla.columna.provee', defaultMessage: 'Provee' });
      default:
        return intl.formatMessage({ id: 'gerenciadorPlanilla.columna.servicio', defaultMessage: 'Servicio' });
    }
  };

  const hayErrores = resultado && resultado.errores && resultado.errores.length > 0;
  const titulo = esItems
    ? intl.formatMessage({ id: 'gerenciadorPlanilla.titulo.items', defaultMessage: 'Importar ítems por servicio — {proveedor}' }, { proveedor: nombreProveedor })
    : intl.formatMessage({ id: 'gerenciadorPlanilla.titulo.servicios', defaultMessage: 'Importar servicios — {proveedor}' }, { proveedor: nombreProveedor });

  return (
    <Modal
      open={open}
      onClose={cerrarPorUsuario}
      closeAfterTransition
      // La confirmación de SweetAlert se monta fuera de este modal: sin esto, el modal le robaría el foco y con
      // el teclado no se llegaría a sus botones.
      disableEnforceFocus
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 1000 }}
      style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', marginTop: '10vh', overflowY: 'auto' }}
    >
      <Grow in={open}>
        <div className="modal-dialog modal-lg" tabIndex="-1" id="gerenciador_planilla_modal" role="dialog" aria-labelledby="gerenciadorPlanillaLabel">
          <div className="modal-content">
            <div className="modal-header bg-fleet">
              <h4 className="modal-title text-white" id="gerenciadorPlanillaLabel">
                <i className="fa fa-file-excel-o mr-1" />{titulo}
              </h4>
              <button type="button" onClick={cerrarPorUsuario} disabled={importando} className="close" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="d-flex align-items-center">
                <input
                  ref={inputArchivo}
                  id="gerenciadorPlanillaArchivo"
                  type="file"
                  accept=".xlsx,.csv"
                  style={{ display: 'none' }}
                  onChange={handleArchivo}
                />
                <button type="button" className="btn btn-outline-primary btn-sm mr-2" disabled={importando} onClick={() => inputArchivo.current && inputArchivo.current.click()}>
                  <i className="fa fa-upload mr-1" />
                  <FormattedMessage id="gerenciadorPlanilla.seleccionar_archivo" defaultMessage="Seleccionar archivo .xlsx / .csv" />
                </button>
                {archivo && (
                  <span className="text-muted" id="gerenciadorPlanillaNombre">
                    <i className={errorGeneral ? 'fa fa-exclamation-circle text-danger mr-1' : 'fa fa-check-circle text-success mr-1'} />
                    {archivo.name}
                  </span>
                )}
              </div>
              <p className="text-muted mt-1 mb-0" style={{ fontSize: 12 }}>
                {gerenciadorId ? (
                  <FormattedMessage
                    id="gerenciadorPlanilla.ayuda"
                    defaultMessage="Use una planilla exportada de este proveedor, con la columna Provee en SI o NO. Lo que no figura en la planilla queda como está."
                  />
                ) : (
                  // Alta: todavía no hay proveedor; la planilla sale de lo que tiene el formulario.
                  <FormattedMessage
                    id="gerenciadorPlanilla.ayuda_alta"
                    defaultMessage="Use una planilla exportada desde esta pantalla, con la columna Provee en SI o NO. Lo que no figura en la planilla queda como está."
                  />
                )}
              </p>

              {errorGeneral && (
                <div className="text-danger font-weight-bold mt-2" id="gerenciadorPlanillaError">
                  <i className="fa fa-exclamation-triangle mr-1" />{errorGeneral}
                </div>
              )}

              {resultado && (
                <div className="mt-2" id="gerenciadorPlanillaResultado">
                  <div className="alert alert-warning mb-2">
                    <i className="fa fa-exclamation-triangle mr-1" />
                    {resultado.importadas > 0
                      ? <FormattedMessage
                        id="gerenciadorPlanilla.resultado.parcial"
                        defaultMessage="{importadas, plural, one {Se importó # fila} other {Se importaron # filas}}. {conError, plural, one {# fila con error no se importó} other {# filas con errores no se importaron}}."
                        values={{ importadas: resultado.importadas, conError: resultado.conError }}
                      />
                      : <FormattedMessage
                        id="gerenciadorPlanilla.resultado.ninguna"
                        defaultMessage="No se importó ninguna fila: {conError, plural, one {# fila con error} other {# filas con errores}}."
                        values={{ conError: resultado.conError }}
                      />}
                  </div>
                  {hayErrores && (
                    <div>
                      <strong className="text-danger">
                        <FormattedMessage id="gerenciadorPlanilla.filas_no_importadas" defaultMessage="Filas no importadas" />
                      </strong>
                      <div style={{ maxHeight: 320, overflowY: 'auto' }} className="mt-1">
                        <table className="table table-sm mb-0">
                          <thead>
                            <tr>
                              <th style={{ width: 60 }}><FormattedMessage id="gerenciadorPlanilla.columna.fila" defaultMessage="Fila" /></th>
                              <th><FormattedMessage id="gerenciadorPlanilla.columna.servicio" defaultMessage="Servicio" /></th>
                              <th style={{ width: 90 }}><FormattedMessage id="gerenciadorPlanilla.columna.campo" defaultMessage="Campo" /></th>
                              <th><FormattedMessage id="gerenciadorPlanilla.columna.mensaje" defaultMessage="Mensaje" /></th>
                            </tr>
                          </thead>
                          <tbody>
                            {resultado.errores.map((error, indice) => (
                              <tr key={'error_' + indice}>
                                <td>{error.fila}</td>
                                <td>{error.servicio}</td>
                                <td>{etiquetaCampo(error.campo)}</td>
                                <td className="text-danger">{traducir(error.mensaje, error.context)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-danger mr-1" disabled={importando} onClick={cerrarPorUsuario}>
                <i className="fa fa-times-circle mr-1" />
                <FormattedMessage id="gerenciadorPlanilla.cancelar" defaultMessage="Cancelar" />
              </button>
              <button type="button" className="btn btn-fleet" id="gerenciadorPlanillaImportar" disabled={!archivo || !!errorGeneral || importando || !!resultado} onClick={handleImportar}>
                <i className={importando ? 'la la-spinner spinner mr-1' : 'fa fa-upload mr-1'} />
                <FormattedMessage id="gerenciadorPlanilla.importar" defaultMessage="Importar" />
              </button>
            </div>
          </div>
        </div>
      </Grow>
    </Modal>
  );
};

export default GerenciadorPlanillaImportModal;
